import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { apiClient } from '@/core/http/client';
import { usePrincipal } from '@/core/auth/appToken';
import type { ApiError } from '@/models/apiError';
import type { components } from '@/generated/api/schema';
import { dateiablageKeys } from './dateiablage.keys';

/** Wire response for the unread-documents counter endpoint. */
export type UngelesenZaehlerResponse = components['schemas']['UngelesenZaehlerResponse'];

/**
 * The three "Partei"-side organization types the unread counter is meaningful for (e16-02 AK-1/2/7)
 * — Portal support has no notion of "own organization" and the backend answers it fail-closed with
 * `{gesamt: 0, jeVersender: []}` anyway, so there is nothing worth fetching for that org type.
 */
const PARTEI_ORG_TYPES: readonly string[] = ['PRACTICE', 'CUSTOMER_LAB', 'DELABO_LAB'];

async function fetchUngelesenZaehler(): Promise<UngelesenZaehlerResponse> {
  // Non-2xx responses become a typed ApiError before this line is reached — see client.ts. No
  // query parameters: the organization comes exclusively from the app token.
  const { data } = await apiClient.GET('/api/v1/dateiablage/ungelesen-zaehler');
  return data as UngelesenZaehlerResponse;
}

/**
 * Fetches the unread-Dateiablage-document counter for the caller's own organization (e16-02
 * AK-1/2/6/7) — backs the navigation badge (Work-Item 10) and the per-customer unread markers in
 * Kundenverwaltung (Work-Item 11).
 *
 * Deliberately **no `refetchInterval`/`refetchOnWindowFocus`** (Ä4, no polling): the count is kept
 * fresh exclusively through cache invalidation (download/upload/delete/share already invalidate
 * `dateiablageKeys.all`, which this query's key falls under) and through its `staleTime`, which
 * lets a newly mounted observer (e.g. opening Kundenverwaltung) pick up a background refetch for
 * free without this hook requesting it explicitly.
 *
 * `enabled` is gated on the caller's `organizationType` being one of the three {@link
 * PARTEI_ORG_TYPES} — pure fetch-avoidance for a party the backend would answer with a trivial
 * zero count anyway, not a security boundary (the server stays fail-closed regardless).
 */
export function useUngelesenZaehler(): UseQueryResult<UngelesenZaehlerResponse, ApiError> {
  const principal = usePrincipal();
  const enabled = principal !== null && PARTEI_ORG_TYPES.includes(principal.organizationType);

  return useQuery({
    queryKey: dateiablageKeys.ungelesenZaehler(),
    queryFn: fetchUngelesenZaehler,
    enabled,
    staleTime: 30_000,
  });
}
